'use-strict';

import angular, { IHttpService } from 'angular';
import {
  FrontendUser,
  NewUserRequest,
  NewUserResponse,
  RoleAssignment,
  User,
  UserRole,
  UserSubsidiaryAssigment,
} from '../../src/app/@shared-types/user';
import { ExternalCompany } from '../../src/app/@shared-types/user/external';

export interface RoleWithDetails {
  id: UserRole,
  name: string,
  description: string,
}

export interface UserWithRoleAssignments extends User {
  roleAssignments: number[],
  roleAssignmentsText: string,
}

export class UsersAPIService {
  private $http: IHttpService;

  constructor(
    $http: IHttpService,
  ) {
    'ngInject';

    this.$http = $http;
  }

  public getSubsidiaryAssignments = (
    userId: number,
  ): Promise<UserSubsidiaryAssigment[]> => new Promise((resolve) => {
    this.$http
      .get<UserSubsidiaryAssigment[]>(`${API_URL}/api/users/subsidiary-assignments/${userId}`)
      .then((res) => resolve(res.data));
  });

  /** Update a user's role assignments. */
  public updateSubsidiaryAssignments = (
    userId: number,
    subsidiaryAssignments: Omit<UserSubsidiaryAssigment, 'id'>[],
  ): Promise<void> => new Promise((resolve) => {
    this.$http
      .put(`${API_URL}/api/users/subsidiary-assignments/${userId}`, { assignments: subsidiaryAssignments })
      .then(() => resolve());
  });

  public getRoleAssignmentsForUser = (
    userId: number,
  ): Promise<{ user_id: number, role_id: number }[]> => new Promise((resolve) => {
    this.$http
      .get<{ user_id: number, role_id: number }[]>(`${API_URL}/api/users/role-assignment/${userId}`)
      .then((res) => resolve(res.data));
  });

  public getUser = (
    userId: number,
  ): Promise<User> => new Promise((resolve) => {
    this.$http
      .get<User>(`${API_URL}/api/users/user/${userId}`)
      .then((res) => resolve(res.data));
  });

  /** Returns all roles. */
  public getRoles = (): Promise<RoleWithDetails[]> => new Promise((resolve) => {
    this.$http
      .get<RoleWithDetails[]>(`${API_URL}/api/users/role`)
      .then((res) => resolve(res.data));
  });

  public addUser = (
    newUserRequestData: NewUserRequest,
  ): Promise<NewUserResponse> => new Promise((resolve) => {
    this.$http
      .post<NewUserResponse>(`${API_URL}/api/users/user`, newUserRequestData)
      .then((res) => resolve(res.data));
  });

  /** Update a user's role assignments. */
  public updateRoleAssignments = (
    roleAssignments: RoleAssignment[],
  ): Promise<void> => new Promise((resolve) => {
    this.$http
      .put(`${API_URL}/api/users/role-assignment`, roleAssignments)
      .then(() => resolve());
  });

  public deleteUser = (userId: number): Promise<void> => new Promise((resolve, reject) => {
    this.$http
      .delete(`${API_URL}/api/users/user/${userId}`)
      .then(() => resolve())
      .catch((err) => {
        this.apiError('Benutzer konnte nicht gelöscht werden.');
        return reject(err);
      });
  });

  public updateUser = (
    user: FrontendUser,
    password?: string,
  ): Promise<void> => new Promise((resolve) => {
    this.$http
      .put(`${API_URL}/api/users/user`, {
        user,
        password,
      })
      .then(() => resolve());
  });

  /** Returns all users with their role assignments added to them. */
  // eslint-disable-next-line max-len
  public getUsersWithRoles = async (): Promise<UserWithRoleAssignments[]> => {
    const users = await this.getUsers();
    const roles = await this.getRoles();
    const roleAssignments = await this.getRoleAssignments();

    return new Promise((resolve, reject) => {
      const returnUsers: UserWithRoleAssignments[] = [];

      users.forEach((u) => {
        const assignedRoles = roleAssignments.filter((r) => r.user_id === u.id);
        const userRoles = roles.filter((r) => assignedRoles.some((a) => a.role_id === r.id));

        returnUsers.push({
          ...u,
          roleAssignments: userRoles.map((r) => r.id),
          roleAssignmentsText: userRoles.map((r) => r.name).join(', '),
        });
      });

      return resolve(returnUsers);
    });
  };

  public getUsers = (): Promise<User[]> => new Promise((resolve, reject) => {
    this.$http
      .get<User[]>(`${API_URL}/api/users/user`)
      .then((res) => resolve(res.data))
      .catch((err) => {
        this.apiError('Benutzer konnten nicht abgerufen werden.');
        return reject(err);
      });
  });

  public getExternalCompanies = (): Promise<ExternalCompany[]> => new Promise((resolve, reject) => {
    this.$http
      .get<ExternalCompany[]>(`${API_URL}/api/users/external-companies`)
      .then((res) => resolve(res.data))
      .catch((err) => {
        this.apiError('Externe Firmen konnten nicht abgerufen werden.');
        return reject(err);
      });
  });

  public getRoleAssignments = (): Promise<RoleAssignment[]> => new Promise((resolve, reject) => {
    this.$http
      .get<RoleAssignment[]>(`${API_URL}/api/users/role-assignment`)
      .then((res) => resolve(res.data))
      .catch((err) => {
        this.apiError('Benutzer konnten nicht abgerufen werden.');
        return reject(err);
      });
  });

  /** Show an error message and log the error stack to the console */
  private apiError = (message: string): void => {
    // this.toastService.toast(ToastType.ERROR, message);
  };
}

export default angular.module('services.usersApiService', [])
  .service('usersApiService', UsersAPIService)
  .name;
